import type { NostrEvent } from '@nostrify/nostrify';

/**
 * NIP-51 standard list kind 10102 — "Good wiki relays": relays the author
 * deems to host useful NIP-54 wiki articles. Being a standard list
 * (10000–29999 replaceable), each author has exactly one, no `d` tag.
 *
 * Wikistar aggregates everyone's kind 10102 into a single community
 * "Publishers list" (`/publishers`): the union of all `relay` tags, with
 * per-relay attribution (who vouched for it). Relay operators add their
 * own relay to opt into being indexed for wiki content; anyone else can
 * vouch for relays they find useful. Editing = republishing your own
 * list; removing a relay from it = republishing without that tag.
 */
export const GOOD_WIKI_RELAYS_KIND = 10102;

export function isValidWikiRelayList(event: NostrEvent): boolean {
  return event.kind === GOOD_WIKI_RELAYS_KIND;
}

/**
 * Normalizes a relay URL for storage/dedupe: adds the `wss://` scheme
 * when missing and lets `URL` canonicalize (lowercased host, trailing
 * slash). Returns undefined for anything that isn't a plausible
 * ws/wss URL.
 */
export function normalizeRelayUrl(input: string): string | undefined {
  const trimmed = input.trim();
  if (!trimmed) return undefined;
  try {
    const url = new URL(trimmed.includes('://') ? trimmed : `wss://${trimmed}`);
    if (url.protocol !== 'wss:' && url.protocol !== 'ws:') return undefined;
    return url.toString();
  } catch {
    return undefined;
  }
}

/** The relay URLs a kind 10102 event recommends — normalized, deduped, order preserved. */
export function relayListUrls(event: NostrEvent): string[] {
  const urls: string[] = [];
  for (const [name, value] of event.tags) {
    if (name !== 'relay' || !value) continue;
    const normalized = normalizeRelayUrl(value);
    if (normalized && !urls.includes(normalized)) urls.push(normalized);
  }
  return urls;
}
